import { HttpProvider } from "../http/http.provider";
import { ServiceEndpointConstants } from "../ServiceUtil/consts/index";
import { Observable, of } from "rxjs";
import { share, map, catchError } from "rxjs/operators";
import { IHttpRequestConfig } from "../http/types/http.types";
import {
    IAntipasResponse,
    IAntipasSwipeResponse,
    IAntipasDailyAbsentResponse,
    IUnmappedEmployeeResponse,
    IAntipasSyncLogResponse,
    IAbsenceResponse,
    INewEmployeeAbsence,
    IEmployeeAbsence,
    IAccrualHistoryResponse,
    IAccrual,
    IEmployeeAbsenceHistoryResponse,
    IAccuralEmpSummaryRes,
    IEmpAccuralHistoryRes
} from "./attendance.interface";

/**
 * Authentication Delegate - handles HTTP communication for authentication
 */
export class AttendanceDelgate {
    constructor(private http: HttpProvider) {
    }
    public getAntipasRawData(fromDate: string, toDate: string): Observable<IAntipasResponse> {
        const config: IHttpRequestConfig = {
            params: {
                FromDate: fromDate,
                ToDate: toDate
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.ANTIPAS.GET_RAW_DATA, null, config).pipe(
            map((response: any) => {
                return {
                    data: response.data.data,
                    success: response.data.success
                };
            }),
            share());
    }

    public getAntipasSwipeData(orgId: string, fromDate: string, toDate: string, employeeId: string): Observable<IAntipasSwipeResponse> {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId,
                FromDate: fromDate,
                ToDate: toDate,
                EmployeeId: employeeId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.ANTIPAS.GET_SWIPE_DATA, null, config).pipe(
            map((response: any) => {
                return {
                    data: response.data.data,
                    success: response.data.success
                };
            }),
            share());
    }
    public getAntipasDailySwipeData(orgId: string, date: string, employeeIds: string[]): Observable<IAntipasSwipeResponse> {
        const empParams = employeeIds.map(id => `EmployeeIds=${encodeURIComponent(id)}`).join('&');
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId,
                Date: date,
            }
        };
        return this.http.get(`${ServiceEndpointConstants.endpoints.ANTIPAS.GET_DAILY_SWIPE}?${empParams}`, null, config).pipe(
            map((response: any) => {
                return {
                    data: response.data.data,
                    success: response.data.success
                };
            }),
            share());
    }
    public getAntipasDailyAbsentData(orgId: string, date: string): Observable<IAntipasDailyAbsentResponse> {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId,
                Date: date
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.ANTIPAS.GET_ABSENTS, null, config).pipe(
            map((response: any) => {
                return {
                    data: response.data.data,
                    success: response.data.success
                };
            }),
            share());
    }
    public getAntipasUnmappedEmployeeData(orgId: string): Observable<IUnmappedEmployeeResponse> {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.ANTIPAS.GET_UNMAPPED_EMPLOYEE, null, config).pipe(
            map((response: any) => {
                return {
                    data: response.data.data,
                    success: response.data.success
                }
            })
        )
    }
    public getAntipasSyncLogData(orgId: string): Observable<IAntipasSyncLogResponse> {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.ANTIPAS.GET_SYNC_LOGS, null, config).pipe(
            map((response: any) => {
                return {
                    data: response.data.data,
                    success: response.data.success
                }
            })
        )
    }

    public getPendingAbsencesByOrgId(orgId: string): Observable<IAbsenceResponse> {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId
            }
        };

        return this.http.get(ServiceEndpointConstants.endpoints.ATTENDANCE.GET_PENDING_ABSENCES_BY_ORG, null, config).pipe(
            map((response: any) => {
                return {
                    attendances: response.data.attendances,
                    success: response.data.success
                };

            }),
            share());
    }
    public getAttendanceIdById(attendanceId: string): Observable<IAbsenceResponse> {
        const config: IHttpRequestConfig = {
            params: {
                AttendanceId: attendanceId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.ATTENDANCE.GET_ATTENDANCE_BY_ID, null, config).pipe(
            map((response: any) => {
                return {
                    attendance: response.data.attendance,
                    attendanceId: attendanceId,
                    success: response.data.success
                };
            }),
            share());
    }
    public createNewEmployeeAbsence(newAbsence: INewEmployeeAbsence): Observable<IAbsenceResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.ATTENDANCE.CREATE_ABSENCE, newAbsence).pipe(

            map((response: any) => {
                return {
                    attendances: response.data.attendances,
                    success: response.data.success
                };
            }),
            catchError(error => {
                return of({
                    success: error.data.success,
                    errorString: error.data.errorString
                });

            }),
            share());
    }
    public updateEmployeeAbsence(newAbsence: IEmployeeAbsence): Observable<IAbsenceResponse> {
        return this.http.post(ServiceEndpointConstants.endpoints.ATTENDANCE.UPDATE_ABSENCE, newAbsence).pipe(

            map((response: any) => {
                return {
                    attendance: AttendanceDelgate.normalizeAbsence(response.data.attendance),
                    success: response.data.success
                };
            }),
            catchError(error => {
                return of({
                    success: error.data.success,
                    errorString: error.data.errorString
                });

            }),
            share());
    }
    public approveAbsence(attendanceId: string, managerComments: string, approvedBy: string, applySandwichPenalty: boolean): Observable<IAbsenceResponse> {
        const req = {
            AttendanceId: attendanceId,
            ManagerComments: managerComments,
            ApprovedBy: approvedBy,
            ApplySandwichPenalty: applySandwichPenalty
        };
        return this.http.post(ServiceEndpointConstants.endpoints.ATTENDANCE.APPROVE_ATTENDANCE, req).pipe(
            map((response: any) => {
                return {
                    attendance: response.data.attendance,
                    success: response.data.success
                };
            }),
            share());
    }
    public rejectAbsence(attendanceId: string, managerComments: string, rejectedBy: string): Observable<IAbsenceResponse> {
        const req = {
            AttendanceId: attendanceId,
            ManagerComments: managerComments,
            RejectedBy: rejectedBy
        };
        return this.http.post(ServiceEndpointConstants.endpoints.ATTENDANCE.REJECT_ATTENDANCE, req).pipe(
            map((response: any) => {
                return {
                    attendance: response.data.attendance,
                    success: response.data.success
                };
            }),
            share());
    }
    public bulkApproveAbsence(attendanceIds: string[], managerComments: string, approvedBy: string, applySandwichPenalty: boolean): Observable<IAbsenceResponse> {
        const req = {
            AttendanceIds: attendanceIds,
            ManagerComments: managerComments,
            ApprovedBy: approvedBy,
            ApplySandwichPenalty: applySandwichPenalty
        };
        return this.http.post(ServiceEndpointConstants.endpoints.ATTENDANCE.BULK_APPROVE_ATTENDANCE, req).pipe(
            map((response: any) => {
                return {
                    attendance: response.data.attendance,
                    success: response.data.success
                };
            }),
            share()
        );
    }
    public bulkRejectAbsence(attendanceIds: string[], managerComments: string, rejectedBy: string): Observable<IAbsenceResponse> {
        const req = {
            AttendanceIds: attendanceIds,
            ManagerComments: managerComments,
            RejectedBy: rejectedBy
        };
        return this.http.post(ServiceEndpointConstants.endpoints.ATTENDANCE.BULK_REJECT_ATTENDANCE, req).pipe(
            map((response: any) => {
                return {
                    attendance: response.data.attendance,
                    success: response.data.success
                };
            }),
            share()
        );
    }
    public getAccrualHistoryByEmployee(employeeId: string, year: number): Observable<IAccrualHistoryResponse> {
        const config: IHttpRequestConfig = {
            params: {
                EmployeeId: employeeId,
                Year: year
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.ATTENDANCE.GET_ACCRUAL_HISTORY_BY_EMPLOYEE, null, config).pipe(
            map((response: any) => {
                return {
                    accruals: response.data.accruals,
                    success: response.data.success
                };
            }),
            share());
    }
    public getAccrualOrgSummary(orgId: string, year: number, month: number): Observable<IAccrualHistoryResponse> {
        const config: IHttpRequestConfig = {
            params: {
                OrgId: orgId,
                Year: year,
                Month: month
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.ATTENDANCE.GET_ACCRUAL_ORG_SUMMARY, null, config).pipe(
            map((response: any) => {
                return {
                    accruals: AttendanceDelgate.normalizeAccruals(response.data.accruals),
                    success: response.data.success
                };
            }),
            share());
    }
    public getEmployeeAbsenceHistoryByDateRange(employeeId: string, fromDate: string, toDate: string): Observable<IEmployeeAbsenceHistoryResponse> {
        const config: IHttpRequestConfig = {
            params: {
                EmployeeId: employeeId,
                FromDate: fromDate,
                ToDate: toDate
            }
        };

        return this.http.get(ServiceEndpointConstants.endpoints.ATTENDANCE.GET_EMPLOYEE_ABSENCE_HISTORY_BY_DATE_RANGE, null, config).pipe(
            map((response: any) => {
                return {
                    attendances: response.data.attendances,
                    success: response.data.success
                };

            }),
            share()
        );
    }
    private static normalizeAccruals(accruals: any[]): IAccrual[] {
        return (accruals || []).map(a => ({
            ...a,
            employeeId: a.employee?.employeeId ?? a.employeeId,
            employeeFullName: a.employee?.employeeFullName ?? a.employee?.fullName ?? ''
        }));
    }

    public getAllAbsencesByReportingManagerId(reportingManagerId: string): Observable<IAbsenceResponse> {
        const config: IHttpRequestConfig = {
            params: {
                ReportingManagerId: reportingManagerId
            }
        };
        return this.http.get(ServiceEndpointConstants.endpoints.ATTENDANCE.GET_PENDING_ABSENCES_BY_MANAGER, null, config).pipe(
            map((response: any) => {
                return {
                    attendances: AttendanceDelgate.normalizeAbsences(response.data.attendances),
                    success: response.data.success
                };
            }),
            share());
    }

    public getEmployeeAbsenceById(employeeId: string, year: number): Observable<IAbsenceResponse> {
        const config: IHttpRequestConfig = { params: { EmployeeId: employeeId, Year: year } };
        return this.http.get(ServiceEndpointConstants.endpoints.ATTENDANCE.GET_ABSENCE_BY_EMPLOYEE, null, config).pipe(
            map((response: any) => response.data),
            share());
    }

    public getEmpYearSummary(employeeId: string, year: number): Observable<IAccuralEmpSummaryRes> {
        const config: IHttpRequestConfig = { params: { EmployeeId: employeeId, Year: year } };
        return this.http.get(ServiceEndpointConstants.endpoints.ATTENDANCE.GET_EMP_YEAR_SUMMARY, null, config).pipe(
            map((response: any) => response.data),
            share());
    }

   public getEmpAccrualHistoryByDateRange(employeeId: string, fromDate: string, toDate: string): Observable<IEmpAccuralHistoryRes> {
    const config: IHttpRequestConfig = {
        params: {
            EmployeeId: employeeId,
            FromDate: fromDate,
            ToDate: toDate
        }
    };
    return this.http.get(ServiceEndpointConstants.endpoints.ATTENDANCE.GET_EMP_ACCRUAL_HISTORY_BY_DATE_RANGE, null, config).pipe(
        map((response: any) => response.data),
        share());
}

    public static normalizeAbsences(employeeAbsences: any): IEmployeeAbsence[] {
        return employeeAbsences.map(employeeAbsence => {
            return this.normalizeAbsence(employeeAbsence);
        });
    }
    public static normalizeAbsence(employeeAbsence: any): IEmployeeAbsence {
        employeeAbsence.employeeId = employeeAbsence.employeeId ? employeeAbsence.employeeId : '';
        employeeAbsence.absenceFrom = employeeAbsence.absenceFrom ? employeeAbsence.absenceFrom : '';
        employeeAbsence.absenceTo = employeeAbsence.absenceTo ? employeeAbsence.absenceTo : '';
        employeeAbsence.comments = employeeAbsence.comments ? employeeAbsence.comments : '';
        employeeAbsence.approvedBy = employeeAbsence.approvedBy ? employeeAbsence.approvedBy : '';
        // employeeAbsence.employeeFullName = employeeAbsence.employeeFullName ? employeeAbsence.employeeFullName : '';
        employeeAbsence.lastModifyBy = employeeAbsence.lastModifyBy ? employeeAbsence.lastModifyBy : '';
        employeeAbsence.lastModifyDateTime = employeeAbsence.lastModifyDateTime ? employeeAbsence.lastModifyDateTime : '';
        employeeAbsence.approvalStatus = employeeAbsence.approvalStatus ? employeeAbsence.approvalStatus : '';
        employeeAbsence.fullName = employeeAbsence.employee ? employeeAbsence.employee.fullName : '';
        employeeAbsence.absenceTypeName = employeeAbsence.absenceTypeName ? employeeAbsence.absenceTypeName : '';
        return employeeAbsence;
    }

}